import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductieOverzicht } from '../api/api';
import { fmtId } from '../util/format';
import VerversPill from '../components/VerversPill';

const STATUS_LABEL = { compleet: 'Compleet', deels: 'Deels', niets: 'Niets ontvangen' };
const STATUS_CHIP = { compleet: 'chip-success', deels: 'chip-warning', niets: 'chip-danger' };
const STATUS_BALK = { compleet: 'var(--success)', deels: 'var(--warning)', niets: 'var(--danger)' };

function nlDatum(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Uitnodiging({ u }) {
  if (!u || u.staat === 'niet_verstuurd') {
    return <span style={{ color: 'var(--text-muted)' }}>nog niet verstuurd</span>;
  }
  if (u.staat === 'gebruikt') {
    return <span style={{ color: 'var(--success)' }}>ingevuld op {nlDatum(u.gebruikt_op)}</span>;
  }
  if (u.staat === 'verlopen') {
    return <span style={{ color: 'var(--danger)' }}>link verlopen ({nlDatum(u.verstuurd_op)})</span>;
  }
  return <span style={{ color: 'var(--text-soft)' }}>verstuurd {nlDatum(u.verstuurd_op)}</span>;
}

export default function ProductieOverzicht() {
  const navigate = useNavigate();
  const [jaar, setJaar] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);
  const [bezig, setBezig] = useState(true);
  const [error, setError] = useState(null);
  const [alleenOpen, setAlleenOpen] = useState(true);

  // laad(stil): bij stil=true blijft de oude data staan (geen volledige
  // spinner) terwijl de nieuwe wordt opgehaald — voor de vernieuwknop.
  const laad = useCallback(async (stil = false) => {
    if (!stil) setBezig(true);
    setError(null);
    try {
      const res = await getProductieOverzicht(jaar);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      if (!stil) setBezig(false);
    }
  }, [jaar]);

  useEffect(() => { laad(false); }, [laad]);

  const zichtbareRijen = useMemo(() => {
    if (!data) return [];
    return alleenOpen ? data.rijen.filter((r) => r.status !== 'compleet') : data.rijen;
  }, [data, alleenOpen]);

  const s = data?.samenvatting;
  const totaal = s?.van_toepassing || 0;

  const tegels = s ? [
    { label: 'Van toepassing', val: totaal, kleur: 'var(--text)' },
    { label: 'Compleet', val: s.compleet, kleur: 'var(--success)' },
    { label: 'Deels', val: s.deels, kleur: 'var(--warning)' },
    { label: 'Niets ontvangen', val: s.niets, kleur: 'var(--danger)' },
  ] : [];

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <button className="btn btn-ghost" onClick={() => navigate('/beheertools')} style={{ marginBottom: 12 }}>
            ← Terug naar beheertools
          </button>
          <h1 className="page-title">Opvraag grondwaterproductie</h1>
          <p className="page-subtitle">
            Status van de jaaropgaven per inrichting. Inrichtingen die in het gekozen jaar nog niet bestonden, vallen buiten beschouwing.
          </p>
        </div>
        <div className="row gap-sm">
          <label style={{ fontSize: 14, color: 'var(--text-soft)' }}>Opgavejaar</label>
          <select
            value={jaar}
            onChange={(e) => setJaar(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            {(data?.beschikbare_jaren || [jaar]).map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
          <div className="ververs-wrap" style={{ marginLeft: 4 }}>
            <VerversPill onVervers={() => laad(true)} />
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {bezig && <p style={{ color: 'var(--text-muted)' }}>Bezig met laden…</p>}

      {!bezig && data && (
        <>
          <div className="overzicht-tegels">
            {tegels.map((t) => (
              <div key={t.label} className="card card-compact overzicht-tegel">
                <div className="overzicht-tegel-label">{t.label}</div>
                <div className="overzicht-tegel-cijfer" style={{ color: t.kleur }}>{t.val}</div>
              </div>
            ))}
          </div>

          {totaal > 0 && (
            <div className="card card-compact" style={{ marginBottom: 20 }}>
              <div className="overzicht-balk">
                {['compleet', 'deels', 'niets'].map((st) => {
                  const n = s[st];
                  if (!n) return null;
                  return (
                    <div
                      key={st}
                      title={`${STATUS_LABEL[st]}: ${n}`}
                      style={{ width: `${(n / totaal) * 100}%`, background: STATUS_BALK[st] }}
                    />
                  );
                })}
              </div>
              <div className="overzicht-legenda">
                {['compleet', 'deels', 'niets'].map((st) => (
                  <span key={st}>
                    <span className="overzicht-legenda-stip" style={{ background: STATUS_BALK[st] }} />
                    {STATUS_LABEL[st]}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="row gap-sm" style={{ marginBottom: 12 }}>
            <label className="row gap-sm" style={{ fontSize: 14, color: 'var(--text-soft)', cursor: 'pointer' }}>
              <input type="checkbox" checked={alleenOpen} onChange={(e) => setAlleenOpen(e.target.checked)} />
              Alleen openstaande tonen
            </label>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{zichtbareRijen.length} van {totaal} getoond</span>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>INRICHTING</th>
                  <th>HOUDER</th>
                  <th>STATUS</th>
                  <th>VOORTGANG</th>
                  <th>UITNODIGING</th>
                </tr>
              </thead>
              <tbody>
                {zichtbareRijen.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                      {totaal === 0
                        ? `Geen opgaven van toepassing voor ${jaar}.`
                        : `Alle opgaven voor ${jaar} zijn compleet.`}
                    </td>
                  </tr>
                ) : (
                  zichtbareRijen.map((r) => (
                    <tr key={r.inrichting_id} className="clickable" onClick={() => navigate(`/beregening/inrichtingen/${r.inrichting_id}`)}>
                      <td>
                        <span className="mono mono-id">{fmtId(r.inrichting_id)}</span>{' '}
                        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.rechtstype}</span>
                      </td>
                      <td>
                        <strong>{r.houder}</strong>
                        {!r.heeft_email && (
                          <span className="chip chip-danger" style={{ marginLeft: 8 }} title="Geen e-mailadres bekend">geen e-mail</span>
                        )}
                      </td>
                      <td><span className={`chip ${STATUS_CHIP[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                      <td style={{ color: 'var(--text-soft)' }}>{r.bekend} / {r.van_toepassing} kwartalen</td>
                      <td style={{ fontSize: 13.5 }}><Uitnodiging u={r.uitnodiging} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
